import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { FileUpload } from '../../../../../components/ui/FileUpload';
import { Upload, FileText, X, AlertCircle, CheckCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { AddMaterial, getPreSignedUrlToUpload } from '../../../../../redux/Project/materialSaga';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../../components/ui/tabs';
import Notification from '../../../../../components/ui/custom-notification';
import customNotification from '../../../../../components/ui/custom-notification';
import { convertFeetToInches } from '../../../../../utils/unitUtils';

interface UploadMaterialsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete?: () => void;
  setupData: any;
  project: any;
  pipeSizesData: any[];
  documentTags: any[];
  setDocumentTags: (tags: any[]) => void;
}

const MATERIAL_CATEGORIES = {
  'pipe': 'Pipe',
  'forge': 'Forge',
  'gas': 'Gas',
  'wireLot': 'Wire Lot',
  'coating': 'Coating'
} as const;

export const UploadMaterialsDialog: React.FC<UploadMaterialsDialogProps> = ({
  isOpen,
  onClose,
  onUploadComplete,
  setupData,
  project,
  pipeSizesData,
  documentTags,
  setDocumentTags
}) => {
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [documentFiles, setDocumentFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [excelData, setExcelData] = useState<any>({});
  const [matchedDocuments, setMatchedDocuments] = useState<any>({});
  const [isProcessingExcel, setIsProcessingExcel] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('pipe');
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [localDocumentTags, setLocalDocumentTags] = useState<any[]>(documentTags);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Convert Excel serial date to JavaScript Date
  const convertDate = (excelDate: any): string => {
    if (!excelDate) return '';

    try {
      // If it's already a string, try to parse it
      if (typeof excelDate === 'string') {
        const parsedDate = new Date(excelDate);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.toISOString().split('T')[0]; // Return YYYY-MM-DD format
        }
        return excelDate; // Return as is if it's already a valid date string
      }

      // If it's a number (Excel serial date)
      if (typeof excelDate === 'number') {
        // Excel serial date starts from 1900-01-01, but Excel incorrectly treats 1900 as a leap year
        // So we need to adjust for this
        const excelEpoch = new Date(1900, 0, 1); // January 1, 1900
        const millisecondsPerDay = 24 * 60 * 60 * 1000;

        // Excel serial date calculation
        const date = new Date(excelEpoch.getTime() + (excelDate - 2) * millisecondsPerDay);

        // Format as YYYY-MM-DD
        return date.toISOString().split('T')[0];
      }

      return '';
    } catch (error) {
      console.error('Error converting date:', error);
      return '';
    }
  };
  // Convert document tags to options for manufacturer MultiSelect components
  // This is used for Coating, Gas, and Wire Lot categories
  const manufacturerTagOptions = useMemo(() =>
    localDocumentTags
      .filter((tag: any) => tag.tags_type === 'MATERIAL_MANUFACTURER_TAGS')
      .map(tag => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name
      })), [localDocumentTags]
  );

  // Convert document tags to options for the MultiSelect component
  // value: string representation of tag ID, label: tag name
  const pipeGradeOptions = useMemo(() =>
    localDocumentTags
      .filter((tag: any) => tag.tags_type === 'MATERIAL_PIPE_GRADE')
      .map(tag => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name
      })), [localDocumentTags]
  );
  const workOrderNumberOptions = useMemo(() =>
    localDocumentTags
      .filter((tag: any) => tag.tags_type === 'MATERIAL_WORK_ORDER_NUMBER_TAGS' && tag.project_id === project.projectId)
      .map(tag => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name
      })), [localDocumentTags]
  );



  const poNumberOptions = useMemo(() =>
    localDocumentTags
      .filter((tag: any) => tag.tags_type === 'MATERIAL_PO_NUMBER_TAGS' && tag.project_id === project.projectId)
      .map(tag => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name
      })), [localDocumentTags]
  );

  const locationOptions = useMemo(() =>
    localDocumentTags
      .filter((tag: any) => tag.tags_type === 'MATERIAL_LOCATION_TAGS')
      .map(tag => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name
      })), [localDocumentTags]
  );


  const convertInchesToDecimal = (inchesStr: string): number => {
    if (!inchesStr) return 0;
    const parts = inchesStr.trim().split(" ");
    let total = 0;

    for (const part of parts) {
      if (part.includes("/")) {
        const [num, den] = part.split("/").map(Number);
        total += num / den;
      } else {
        total += parseFloat(part);
      }
    }
    return total;
  };
  const convertLengthToFeet = (lengthString: string): number => {
    if (!lengthString || typeof lengthString !== 'string') return 0;

    try {
      // Clean string (remove quotes, spaces)
      const cleanString = lengthString.replace(/["]/g, '').trim();

      let feet = 0;
      let inches = 0;

      // Match feet part (e.g., 40')
      const feetMatch = cleanString.match(/(\d+)\s*'/);
      if (feetMatch) {
        feet = parseInt(feetMatch[1], 10);
      }

      // Match inches part (e.g., 5 7/8 or 11/16 or 19)
      const inchMatch = cleanString.match(/(\d+(?:\s+\d+\/\d+)?|\d+\/\d+)\s*$/);
      if (inchMatch) {
        inches = convertInchesToDecimal(inchMatch[1]);
      }

      const totalFeet = feet + (inches / 12);

      //  console.log(`"${lengthString}" => ${totalFeet.toFixed(3)} ft`);
      return parseFloat(totalFeet.toFixed(4));
    } catch (error) {
      console.error('Error converting length to feet:', error);
      return 0;
    }
  };


  // Validation rules for different material categories
  const validationRules = {
    pipe: ['pipeNo'],
    forge: ['forgeNRI'],
    gas: ['gasSerialNumber', 'gasLotNumber'],
    wireLot: ['wireLotNumber'],
    coating: ['coatingLotNumber']
  };
  // Field labels for error messages
  const fieldLabels = {
    // status: 'Status',
    pipeNRI: 'Pipe NRI',
    pipeNo: 'Pipe No',
    pipeHeatNumber: 'Heat Number',
    pipeLength: 'Length',
    pipeOuterDiameter: 'Outer Diameter',
    pipeWallThickness: 'Wall Thickness',
    forgeNRI: 'Forge NRI',
    forgeOrderNumber: 'Forge Order Number',
    forgeSerialNumber: 'Forge Serial Number',
    forgeHeatNumber: 'Forge Heat Number',
    gasSerialNumber: 'Gas Serial Number',
    gasType: 'Gas Type',
    gasLotNumber: 'Gas Lot Number',
    wireLotNumber: 'Wire Lot Number',
    wireType: 'Wire Type',
    coatingLotNumber: 'Coating Lot Number',
    coatingManufacturer: 'Coating Manufacturer',
    coatingBatchNumber: 'Coating Batch Number',

    wireManufacturer: 'Wire Manufacturer',
    gasManufacturer: 'Gas Manufacturer',
    forgeManufacturer: 'Forge Manufacturer',

    wireBatchNumber: 'Wire Batch Number',
    gasBatchNumber: 'Gas Batch Number',
    forgeBatchNumber: 'Forge Batch Number',

  };
  // Reset all fields when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Reset all state when modal closes
      setExcelFile(null);
      setDocumentFiles([]);
      setExcelData({});
      setMatchedDocuments({});
      setIsUploading(false);
      setIsProcessingExcel(false);
      setSelectedCategory('pipe');
      setAvailableCategories([]);
    }
  }, [isOpen]);


  // Sync local state with parent state when documentTags change
  useEffect(() => {
    setLocalDocumentTags(documentTags);
  }, [documentTags]);

  // Initialize local state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setLocalDocumentTags(documentTags);
    }
  }, [isOpen, documentTags]);

  const handleExcelFileSelect = (files: FileList | null) => {
    if (files && files.length > 0) {
      const file = files[0];
      // Check if it's an Excel file
      if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.name.endsWith('.xlsx') ||
        file.name.endsWith('.xls')) {
        setExcelFile(file);
        processExcelFile(file);
        Notification.success('Excel template uploaded successfully');
      } else {
        Notification.error('Please upload an Excel file (.xlsx or .xls)');
      }
    }
  };

  // Validation helper function
  const validateMaterialFields = (category: string, material: any): string | null => {
    const requiredFields = validationRules[category as keyof typeof validationRules];

    // Check if validation rules exist for this category
    if (!requiredFields || !Array.isArray(requiredFields)) {
      return null;
    }

    for (const field of requiredFields) {
      // Check if field is at top level (like status) or in details
      const value = material[field] || material.details?.[field];

      if (!value || (typeof value === 'string' && value.trim() === '') ||
        (typeof value === 'number' && value <= 0)) {
        const errorMessage = `${fieldLabels[field as keyof typeof fieldLabels]} is required`;

        return errorMessage;
      }
    }

    return null;
  };


  const processExcelFile = async (file: File) => {
    setIsProcessingExcel(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });




      // Process each category sheet
      const processedData: any = {};
      const processedMatches: any = {};

      for (const category of Object.keys(MATERIAL_CATEGORIES)) {
        const sheetName = MATERIAL_CATEGORIES[category as keyof typeof MATERIAL_CATEGORIES];
        const worksheet = workbook.Sheets[sheetName];

        if (!worksheet) {
          continue;
        }

        const data = XLSX.utils.sheet_to_json(worksheet);


        // Convert data based on category
        const convertedData = data.map((row: any) => {
          const baseData = {
            pipeSelection: row['Pipe Size *'] || row['Pipe Size'] || row['PipeSelection'] || row['pipe_selection'],
            status: row['Status'] || 'Available',
            materialCategory: category
          };
         console.log('baseData', baseData);
          // Add category-specific fields
          switch (category) {
            case 'pipe':
              return {
                ...baseData,
                fileName: row['File Name'] || row['fileName'],
                documentTags: row['Document Tags'] || row['documentTags'],
                pipeNRI: row['Pipe NRI *'] || row['NRI'] || row['Pipe NRI'] || row['pipeNRI'],
                pipeNo: row['Pipe No *'] || row['Pipe No'] || row['PipeNo'] || row['pipeNo'],
                pipeId: row['Pipe Id'] || row['PipeId'] || row['pipeId'],
                pipeHeatNumber: row['Heat Number'] || row['HeatNumber'] || row['heatNumber'],
                pipeLength: convertLengthToFeet(row['Length (ft)'] || row['Length'] || row['Length (inches)']),
                pipeLengthReceived: convertLengthToFeet(row['Length Received (ft)'] || row['Length Received'] || row['Length Received (inches)']),
                pipeLengthRemaining: convertLengthToFeet(row['Length Remaining (ft)'] || row['Length Remaining'] || row['Length Remaining (inches)']),
                pipeWeightPerFt: convertLengthToFeet(row['Weight Per Ft (in)'] || row['Weight Per Ft'] || row['Weight Per Ft (inches)']),
                pipeOuterDiameter: row['Outer Diameter (in)'] || row['Outer Diameter'] || row['Outer Diameter (inches)'] || row['outerDiameter'],
                pipeWallThickness: row['Wall Thickness (in)'] || row['Wall Thickness'] || row['Wall Thickness (inches)'] || row['wallThickness'],
                pipeProjectStage: row['Project Stage'] || row['projectStage'],
                marking: row['Marking'] || row['marking'],
                notes: row['Notes'] || row['notes'],
                date: convertDate(row['Date']),
                projectStage: row['Project Stage'] || row['projectStage'],
                purchaseOrderNumber: row['Purchase Order Number'] || row['purchaseOrderNumber'],
                workOrderNumber: row['Work Order Number'] || row['workOrderNumber'],
                location: row['Location'] || row['location'],
                pipeGrade: row['Pipe Grade'] || row['pipeGrade'],
                manufacturer: row['Manufacturer'] || row['manufacturer'],
                //  assemblyNumber: row['Assembly Number'] || row['AssemblyNumber'] || row['assemblyNumber'],
                //manufacturer: row['Manufacturer'] || row['manufacturer'],
                //batchNumber: row['Batch Number'] || row['BatchNumber'] || row['batchNumber']
              };
            case 'forge':
              return {
                ...baseData,
                fileName: row['File Name'] || row['fileName'],
                forgeNRI: row['Forge NRI *'] || row['NRI'] || row['Forge NRI'] || row['forgeNRI'],
                forgeOrderNumber: row['Forge Order Number *'] || row['Order Number'] || row['Forge Order Number'] || row['orderNumber'],
                forgeSerialNumber: row['Forge Serial Number *'] || row['Serial Number'] || row['Forge Serial Number'] || row['serialNumber'],
                forgeHeatNumber: row['Heat Number'] || row['Forge Heat Number'] || row['heatNumber'],
                forgeLength: convertLengthToFeet(row['Length (ft)'] || row['Length'] || row['Length (inches)']),
                forgeLengthReceived: convertLengthToFeet(row['Length Received (ft)'] || row['Length Received'] || row['Length Received (inches)']),
                forgeLengthRemaining: convertLengthToFeet(row['Length Remaining (ft)'] || row['Length Remaining'] || row['Length Remaining (inches)']),
                forgeWeightPerFt: convertLengthToFeet(row['Weight Per Ft (in)'] || row['Weight Per Ft'] || row['Weight Per Ft (inches)']),
                forgeOuterDiameter: row['Outer Diameter (in)'] || row['Outer Diameter'] || row['Outer Diameter (inches)'] || row['outerDiameter'],
                forgeWallThickness: row['Wall Thickness (in)'] || row['Wall Thickness'] || row['Wall Thickness (inches)'] || row['wallThickness'],
                date: convertDate(row['Date']),
                purchaseOrderNumber: row['Purchase Order Number'] || row['purchaseOrderNumber'],
                workOrderNumber: row['Work Order Number'] || row['workOrderNumber'],
                forgeGrade: row['Forge Grade'] || row['forgeGrade'],
                manufacturer: row['Manufacturer'] || row['manufacturer'],
                notes: row['Notes'] || row['notes'],
                location: row['Location'] || row['location'],
                projectStage: row['Project Stage'] || row['projectStage'],
                //  assemblyNumber: row['Assembly Number'] || row['AssemblyNumber'] || row['assemblyNumber'],
                //manufacturer: row['Manufacturer'] || row['manufacturer'],
                //batchNumber: row['Batch Number'] || row['BatchNumber'] || row['batchNumber']
              };
            case 'gas':
              return {
                ...baseData,
                fileName: row['File Name'] || row['fileName'],
                gasSerialNumber: row['Gas Serial Number *'] || row['Serial Number'] || row['Gas Serial Number'] || row['serialNumber'],
                gasLotNumber: row['Lot Number *'] || row['Lot Number'] || row['LotNumber'] || row['lotNumber'],
                gasManufacturer: row['Manufacturer'] || row['manufacturer'],
                gasBatchNumber: row['Batch Number'] || row['BatchNumber'] || row['batchNumber'],
              };
            case 'wireLot':
              return {
                ...baseData,
                fileName: row['File Name'] || row['fileName'],
                wireLotNumber: row['Lot Number *'] || row['Lot Number'] || row['LotNumber'] || row['lotNumber'],
                wireSerialNumber: row['Serial Number'] || row['Wire Serial Number'] || row['serialNumber'],
                wireManufacturer: row['Manufacturer'] || row['manufacturer'],
                wireBatchNumber: row['Batch Number'] || row['BatchNumber'] || row['batchNumber'],
              };
            case 'coating':
              return {
                ...baseData,
                fileName: row['File Name'] || row['fileName'],
                coatingLotNumber: row['Lot Number *'] || row['Lot Number'] || row['LotNumber'] || row['lotNumber'],
                coatingManufacturer: row['Manufacturer'] || row['manufacturer'],
                coatingBatchNumber: row['Batch Number'] || row['BatchNumber'] || row['batchNumber'],
              };
            default:
              return baseData;
          }
        });

        processedData[category] = convertedData;
       // console.log(processedData);

        // Debug: Show what fields were extracted
        if (convertedData.length > 0) {
        }
        // Match documents for this category
        const categoryMatches = convertedData.map((row: any) => {
          const fileName = row.fileName || '';
          const cleanFileName = fileName?.replace(/['"]/g, '').trim();

          // Handle multiple files separated by comma
          let matchedFiles: File[] = [];
          let isMatched = false;

          if (cleanFileName) {
            // Split by comma and trim each filename
            const fileNames = cleanFileName.split(',').map((name: string) => name.trim());
            
            matchedFiles = fileNames.map((fileName: string) => {
              return documentFiles.find(file => {
                const fileFileName = file.name.toLowerCase();
                const excelFileName = fileName.toLowerCase();

                return fileFileName === excelFileName ||
                  fileFileName.includes(excelFileName) ||
                  excelFileName.includes(fileFileName);
              });
            }).filter(Boolean) as File[]; // Remove undefined values

            isMatched = matchedFiles.length > 0;
          }

          return {
            ...row,
            matchedFiles: matchedFiles,
            matchedFile: matchedFiles[0] || null, // Keep first file for backward compatibility
            isMatched: isMatched,
            expectedFileCount: cleanFileName ? cleanFileName.split(',').length : 0,
            matchedFileCount: matchedFiles.length
          };
        });

        processedMatches[category] = categoryMatches;
      }

      setExcelData(processedData);
      setMatchedDocuments(processedMatches);


      // Validate all categories
      for (const category of Object.keys(processedMatches)) {
        const categoryData = processedMatches[category];

        if (categoryData && categoryData.length > 0) {
          for (const item of categoryData) {
            const validationError = validateMaterialFields(category, item);
            if (validationError) {
              Notification.warning('Please fill the required fields', validationError);
              handleRemoveExcelFile();
              return;
            }
          }
        }
      }


      // Set first available category as selected


    } catch (error) {
      Notification.error('Failed to process Excel file');
    } finally {
      setIsProcessingExcel(false);
    }
  };

  const matchDocumentsWithExcel = (excelData: any, documentFiles: any[]) => {

    const processedMatches: any = {};

    Object.keys(excelData).forEach(category => {
      const categoryData = excelData[category];
      const matched = categoryData.map((row: any) => {
        const fileName = row.fileName || '';
        const cleanFileName = fileName?.replace(/['"]/g, '').trim();

        // Handle multiple files separated by comma
        let matchedFiles: File[] = [];
        let isMatched = false;

        if (cleanFileName) {
          // Split by comma and trim each filename
          const fileNames = cleanFileName.split(',').map((name: string) => name.trim());
          
          matchedFiles = fileNames.map((fileName: string) => {
            return documentFiles.find(file => {
              const fileFileName = file.name.toLowerCase();
              const excelFileName = fileName.toLowerCase();

              return fileFileName === excelFileName ||
                fileFileName.includes(excelFileName) ||
                excelFileName.includes(fileFileName);
            });
          }).filter(Boolean) as File[]; // Remove undefined values

          isMatched = matchedFiles.length > 0;
        }

        return {
          ...row,
          matchedFiles: matchedFiles,
          matchedFile: matchedFiles[0] || null, // Keep first file for backward compatibility
          isMatched: isMatched,
          expectedFileCount: cleanFileName ? cleanFileName.split(',').length : 0,
          matchedFileCount: matchedFiles.length
        };
      });

      processedMatches[category] = matched;
    });


    setMatchedDocuments(processedMatches);
  };

  const handleDocumentFilesSelect = (files: FileList | null) => {

    if (files) {
      const newFiles = Array.from(files);

      const updatedFiles = [...documentFiles, ...newFiles];

      setDocumentFiles(updatedFiles);

      // Re-match documents if Excel data exists
      if (Object.keys(excelData).length > 0) {
        matchDocumentsWithExcel(excelData, updatedFiles);
      }
    } else {
    }
  };

  const handleRemoveExcelFile = () => {
    setExcelFile(null);
    setDocumentFiles([]); // Automatically remove all uploaded documents
    setExcelData({});
    setMatchedDocuments({});
    setIsProcessingExcel(false);
    setAvailableCategories([]);
    setSelectedCategory('pipe');
    Notification.success('Excel template and documents removed successfully');
  };

  const handleRemoveDocumentFile = (index: number) => {
    const updatedFiles = documentFiles.filter((_, i) => i !== index);
    setDocumentFiles(updatedFiles);

    // Re-match documents if Excel data exists
    if (Object.keys(excelData).length > 0) {
      matchDocumentsWithExcel(excelData, updatedFiles);
    }
  };

  const extractPipeId = (pipeSelection: string) => {
    if (pipeSelection && pipeSelection.includes(' - [')) {
      const match = pipeSelection.match(/ - \[(\d+)\]/);
      if (match) {
        return parseInt(match[1]);
      }
    }
    return null;
  };

  const getDetails = (matchedMaterial: any, pipeSizeData: any) => {
    if (matchedMaterial.materialCategory === 'pipe') {
      return {
        pipeNRI: matchedMaterial.pipeNRI,
        pipeNo: matchedMaterial.pipeNo?.toString() || '',
        pipePipeId: matchedMaterial.pipeId,
        pipeHeatNumber: matchedMaterial.pipeHeatNumber,
        pipeLength: convertFeetToInches(matchedMaterial.pipeLength),
        pipeLengthRemaining: convertFeetToInches(matchedMaterial.pipeLengthRemaining),
        pipeWeightPerFt: convertFeetToInches(matchedMaterial.pipeWeightPerFt),
        pipeOuterDiameter: matchedMaterial.pipeOuterDiameter,
        pipeWallThickness: matchedMaterial.pipeWallThickness,
        pipeProjectStage: matchedMaterial.pipeProjectStage || "Production",
        pipeMarking: matchedMaterial.marking,
        pipeNotes: matchedMaterial.notes,
        pipePurchaseOrderNumber: matchedMaterial.purchaseOrderNumber,
        pipeWorkOrderNumber: matchedMaterial.workOrderNumber,
        pipeLocation: matchedMaterial.location,
        pipeGrade: [parseInt(pipeGradeOptions.find(option => option.label === matchedMaterial.pipeGrade)?.value) || matchedMaterial.pipeGrade],
        pipeManufacturer: [parseInt(pipeSizeData.manufacturers.find((option: any) => option.manufacturerName === matchedMaterial.manufacturer)?.manufacturerId) || matchedMaterial.manufacturer],
        date: matchedMaterial.date || new Date().toISOString().split('T')[0],
        pipeExtraDetails: {
          pipeLengthReceived: convertFeetToInches(matchedMaterial.pipeLengthReceived),
          pipeLengthRemaining: convertFeetToInches(matchedMaterial.pipeLengthRemaining),
          pipeWeightPerFt: convertFeetToInches(matchedMaterial.pipeWeightPerFt),
          pipeOuterDiameter: matchedMaterial.pipeOuterDiameter,
          pipeWallThickness: matchedMaterial.pipeWallThickness,
          pipeProjectStage: matchedMaterial.pipeProjectStage || "Production",
          pipeMarking: matchedMaterial.marking,
          pipeNotes: matchedMaterial.notes,
          pipePurchaseOrderNumber: matchedMaterial.purchaseOrderNumber,
          pipeWorkOrderNumber: matchedMaterial.workOrderNumber,
          pipeLocation: matchedMaterial.location,
          pipeGrade: matchedMaterial.pipeGrade,
          pipeManufacturer: matchedMaterial.manufacturer,
        }
      }
    }
    else if (matchedMaterial.materialCategory === 'forge') {
      return {
        forgeNRI: matchedMaterial.forgeNRI,
        forgeOrderNumber:  matchedMaterial.workOrderNumber?.toString() || '',
        forgeSerialNumber: matchedMaterial.forgeSerialNumber?.toString() || '',
        forgeHeatNumber: matchedMaterial?.forgeHeatNumber,
        forgeOuterDiameter: matchedMaterial.forgeOuterDiameter,
        forgeWallThickness: matchedMaterial.forgeWallThickness,
        forgeLength: convertFeetToInches(matchedMaterial.forgeLength),
        forgeLengthRemaining: convertFeetToInches(matchedMaterial.forgeLengthRemaining),
        forgeWeightPerFt: convertFeetToInches(matchedMaterial.forgeWeightPerFt),
        date: matchedMaterial.date || new Date().toISOString().split('T')[0],
        forgePurchaseOrderNumber: matchedMaterial.purchaseOrderNumber?.toString() || matchedMaterial.forgeOrderNumber?.toString() || '',
        forgeWorkOrderNumber: matchedMaterial.workOrderNumber?.toString() || matchedMaterial.forgeOrderNumber?.toString() || '',
        forgeLocation: matchedMaterial.location,
        forgeGrade: [parseInt(pipeGradeOptions.find(option => option.label === matchedMaterial.forgeGrade)?.value) || matchedMaterial.forgeGrade],
        forgeNotes: matchedMaterial.notes,
        forgeProjectStage: matchedMaterial.projectStage || "Production",
        forgeManufacturer: [parseInt(pipeSizeData.manufacturers.find((option: any) => option.manufacturerName === matchedMaterial.manufacturer)?.manufacturerId) || matchedMaterial.manufacturer],
        
        forgeExtraDetails: {
          forgeLengthReceived: convertFeetToInches(matchedMaterial.forgeLengthReceived),
          forgeLengthRemaining: convertFeetToInches(matchedMaterial.forgeLengthRemaining),
          forgeWeightPerFt: convertFeetToInches(matchedMaterial.forgeWeightPerFt),
          forgeOuterDiameter: matchedMaterial.forgeOuterDiameter,
          forgeWallThickness: matchedMaterial.forgeWallThickness,
          forgeNotes: matchedMaterial.notes,
          forgeManufacturer: matchedMaterial.manufacturer,
          forgeProjectStage: matchedMaterial.projectStage || "Production",
        }
      }
    }
    else if (matchedMaterial.materialCategory === 'gas') {
      return {
        gasSerialNumber: matchedMaterial?.gasSerialNumber,
        gasLotNumber: matchedMaterial.gasLotNumber,
        gasManufacturer: matchedMaterial.gasManufacturer,
        gasBatchNumber: matchedMaterial?.gasBatchNumber,
        gasExtraDetails: {
          
          gasManufacturer: matchedMaterial.gasManufacturer,
         
        },
        date: matchedMaterial.date || new Date().toISOString().split('T')[0],
      }
    }
    else if (matchedMaterial.materialCategory === 'wireLot') {
      return {
        wireLotNumber: matchedMaterial?.wireLotNumber,
        wireSerialNumber: matchedMaterial?.wireSerialNumber,
        wireManufacturer: matchedMaterial?.wireManufacturer,
        wireBatchNumber: matchedMaterial.wireBatchNumber,
        wireExtraDetails: {
          
          wireManufacturer: matchedMaterial.wireManufacturer,
         
         
        },
        date: matchedMaterial.date || new Date().toISOString().split('T')[0],
      }
    }
    else if (matchedMaterial.materialCategory === 'coating') {
      return {
        coatingLotNumber: matchedMaterial?.coatingLotNumber,
        coatingManufacturer: matchedMaterial?.coatingManufacturer,
        coatingBatchNumber: matchedMaterial.coatingBatchNumber,
        coatingExtraDetails: {
          coatingManufacturer: matchedMaterial.coatingManufacturer,
        },
        date: matchedMaterial.date || new Date().toISOString().split('T')[0],
      }
    }
    else {
      return {};
    }
  }

  const manufacturerOptions = useMemo(() =>
    localDocumentTags
      .filter((tag: any) => tag.tags_type === 'MATERIAL_MANUFACTURER_TAGS')
      .map(tag => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name
      })), [localDocumentTags]
  );

  // Check if there are any missing files that should prevent upload
  const hasMissingFiles = useMemo(() => {
    if (Object.keys(matchedDocuments).length === 0) return false;
    
    for (const categoryData of Object.values(matchedDocuments)) {
      if (Array.isArray(categoryData)) {
        for (const item of categoryData) {
          const hasFileName = item.fileName && item.fileName.trim() !== '';
          if (hasFileName && !item.isMatched) {
            return true;
          }
        }
      }
    }
    return false;
  }, [matchedDocuments]);

  const handleUpload = async () => {
    console.log('handleUpload');
    if (!excelFile) {
      console.log('excelFile not found');
      return;
    }

    if (Object.keys(excelData).length === 0) {
      console.log('No data found in Excel file');
      return;
    }

    // Check for missing files before upload
    if (hasMissingFiles) {
      Notification.error('Cannot upload: Some materials have filenames but no matching files were found. Please upload the missing files or remove the filenames from Excel.');
      return;
    }

    setIsUploading(true);
    const newMaterials: any[] = [];
    console.log('matchedDocuments', matchedDocuments);

    try {
      // Process each category
      for (const category of Object.keys(matchedDocuments)) {
        const categoryData = matchedDocuments[category];

        for (const matchedMaterial of categoryData) {

          // Extract pipe ID from pipe selection
          const pipelineId = extractPipeId(matchedMaterial.pipeSelection.trim());

          if (!pipelineId) {
            customNotification.warning('Pipeline ID not found', 'Please check the Excel template and try again');
            return;
          }

          // Find corresponding pipe size data
          const pipeSizeData = pipeSizesData.find(pipe => pipe.pipeId === pipelineId);
          if (!pipeSizeData) {
            customNotification.warning('Pipe Size Data not found', 'Please check the Excel template and try again');
            return;
          }
          const payload = {
            pipeId: Number(pipelineId),
            projectId: Number(pipeSizeData.projectId),
            projectTypeId: Number(pipeSizeData.projectTypeId),
            materialCategory: category
          };


          let documents: any[] = [];
          // Check if filename is provided and not empty
          const fileName = matchedMaterial.fileName;
          const hasValidFileName = fileName && fileName.trim() !== '';
        
          // Only upload to S3 if there's a matched file and valid filename
          if (matchedMaterial.isMatched && matchedMaterial.matchedFiles && matchedMaterial.matchedFiles.length > 0 && hasValidFileName) {
            try {
              // Ensure matchedFiles is always an array
              const filesToUpload = Array.isArray(matchedMaterial.matchedFiles) 
                ? matchedMaterial.matchedFiles 
                : [matchedMaterial.matchedFiles].filter(Boolean);
              
              const s3Response = await getPreSignedUrlToUpload(filesToUpload, payload);
              if (s3Response && s3Response.length > 0) {
                documents = s3Response.map((file: any) => ({
                  fileName: file.fileName,
                  s3Key: file.s3Key,
                  version: 1,
                  tags: [],
                  uploadedBy: user.email || ''
                }));
              }
             
            } catch (error) {
              console.log('error', error);
              customNotification.warning(`Failed to upload files for ${matchedMaterial.pipeNRI || matchedMaterial.forgeNRI || matchedMaterial.serialNumber || matchedMaterial.lotNumber}`);
              // Don't continue here, just skip file upload but still create material
            }
          } else if (hasValidFileName && !matchedMaterial.isMatched) {
            // Show warning for materials with filename but no matching file
            const materialIdentifier = matchedMaterial.pipeNRI || matchedMaterial.forgeNRI || matchedMaterial.serialNumber || matchedMaterial.lotNumber;
            console.warn(`File not found for ${materialIdentifier}: Expected "${fileName}" but no matching file was uploaded`);
          }
          // If no filename, silently skip file upload (no message needed)
        
          let materialDetails = {};
          try {
            materialDetails = getDetails(matchedMaterial, pipeSizeData);
          } catch (error) {
            console.error('Error in getDetails:', error);
            materialDetails = {}; // Use empty object if getDetails fails
          }
          
          // Convert to material format
          const materialData = {
            projectTypeId: Number(pipeSizeData.projectTypeId),
            projectType: pipeSizeData.projectType,
            projectSubType: pipeSizeData.projectSubType || "none",
            materialCategory: MATERIAL_CATEGORIES[category as keyof typeof MATERIAL_CATEGORIES] || 'Pipe',
            materialStatus: matchedMaterial.status || "Available",
            pipeId: pipelineId,
            materialDate: matchedMaterial.date || new Date().toISOString().split('T')[0],
            assemblyNumbers: matchedMaterial.assemblyNumbers || [],
            documents: documents, // Empty array if no file matched
            details: materialDetails
          };
          
          newMaterials.push(materialData);
        }
      }

      if (newMaterials.length > 0) {
        const apiPayload = {
          data: newMaterials,
          projectId: project.project.projectId
        };



        const response = await AddMaterial(apiPayload, false);

        if (response) {
          onUploadComplete?.();
          // Reset all fields after successful upload
          setExcelFile(null);
          setDocumentFiles([]);
          setExcelData({});
          setMatchedDocuments({});
          setIsUploading(false);
          setIsProcessingExcel(false);
          setAvailableCategories([]);
          setSelectedCategory('pipe');
          onClose();
        }
      } else {
        Notification.error('No valid materials found to upload');
      }
    } catch (error) {
      Notification.error('Failed to upload materials. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    // Reset all state
    setExcelFile(null);
    setDocumentFiles([]);
    setExcelData({});
    setMatchedDocuments({});
    setIsUploading(false);
    setIsProcessingExcel(false);
    setAvailableCategories([]);
    setSelectedCategory('pipe');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-[800px] max-h-[90vh] p-6 flex flex-col overflow-x-hidden">
        <div className="flex items-center gap-3 mb-6 flex-shrink-0">
          <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <Upload className="h-5 w-5 text-blue-600" />
          </div>
          <DialogTitle className="text-xl font-semibold">
            Upload Materials
          </DialogTitle>
        </div>

        <div className="space-y-6 flex-1 overflow-y-auto overflow-x-hidden">
          {/* Step 1: Upload Excel Template */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                1
              </div>
              <h3 className="text-lg font-medium">Upload Excel Template</h3>
            </div>
            <p className="text-sm text-gray-600">
              Upload the Excel template file that contains the material structure with multiple category sheets.
            </p>

            {excelFile ? (
              <div className="flex items-center p-3 bg-green-50 border rounded-lg">
                <FileText className="h-4 w-4 text-green-600 mr-2" />
                <span className="text-sm text-green-700 flex-1">{excelFile.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveExcelFile}
                  className="h-6 w-6 p-0 hover:bg-red-50 hover:text-red-600"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <FileUpload
                onFileSelect={handleExcelFileSelect}
                accept=".xlsx,.xls"
                multiple={false}
                buttonText="Upload Excel Template"
                variant="outline"
                size="lg"
                className="w-full"
              />
            )}
          </div>

          {/* Step 2: Upload Documents */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-gray-300 text-white rounded-full flex items-center justify-center text-sm font-medium">
                2
              </div>
              <h3 className="text-lg font-medium">Upload Documents</h3>
            </div>
            <p className="text-sm text-gray-600">
              Upload the material documents (PDF, DOC, etc.) that correspond to the template. Document upload is optional - materials can be uploaded without documents.
            </p>

            <div className="w-full overflow-x-hidden">
              <FileUpload
                onFileSelect={handleDocumentFilesSelect}
                accept=".pdf,.doc,.docx,.xlsx,.xls"
                multiple={true}
                maxFiles={100}
                disabled={!excelFile}
                buttonText="Upload Documents (Optional)"
                variant="outline"
                size="lg"
                onRemoveFile={handleRemoveDocumentFile}
                selectedFiles={documentFiles}
                showFileList={documentFiles.length > 0}
                className="w-full"
              />
            </div>

            {/* Debug: Show current documentFiles count */}
            <div className="text-xs text-gray-500">
              Current files: {documentFiles.length} file(s)
            </div>

            {!excelFile && (
              <div className="flex items-center gap-2 p-3 bg-yellow-50 border rounded-lg">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <span className="text-sm text-yellow-700">
                  Please upload an Excel template first to enable document uploads
                </span>
              </div>
            )}

            {/* Excel Processing Status */}
            {excelFile && isProcessingExcel && (
              <div className="flex items-center gap-2 p-3 bg-blue-50 border rounded-lg">
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm text-blue-700">Processing Excel file...</span>
              </div>
            )}
          </div>

          {/* Matched Documents Display */}
          {Object.keys(matchedDocuments).length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700">Excel Data & Document Matching:</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto overflow-x-hidden border border-gray-200 rounded-lg p-2">
                {Object.keys(matchedDocuments).map(category => {
                  const categoryData = matchedDocuments[category];
                  return categoryData.map((item: any, index: any) => {
                    const hasFileName = item.fileName && item.fileName.trim() !== '';
                    const materialIdentifier = item.pipeNRI || item.forgeNRI || item.serialNumber || item.lotNumber || `Item ${index + 1}`;
                    
                    // If filename is empty, do not render this card
                    if (!hasFileName) {
                      return null;
                    }
                    
                    return (
                      <div key={`${category}-${index}`} className={`p-3 rounded-lg border ${
                        hasFileName && item.isMatched
                          ? 'bg-green-50'
                          : hasFileName && !item.isMatched
                          ? 'bg-red-50'
                          : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-700">
                              {category.toUpperCase()} - {materialIdentifier}
                            </div>
                            {item.fileName && item.fileName.trim() !== '' && (
                              <div className="text-xs text-gray-500">
                                Expected: {item.fileName}
                              </div>
                            )}
                            {hasFileName && item.isMatched && (
                              <div className="text-xs text-green-600 flex items-center gap-1 mt-1">
                                <CheckCircle className="h-3 w-3" />
                                Matched: {item.matchedFileCount}/{item.expectedFileCount} files
                                {item.matchedFiles?.map((file: File, idx: number) => (
                                  <span key={idx} className="ml-1 text-xs">
                                    {file.name}{idx < item.matchedFiles.length - 1 ? ', ' : ''}
                                  </span>
                                ))}
                              </div>
                            )}
                            {hasFileName && !item.isMatched && (
                              <div className="text-xs text-red-600 flex items-center gap-1 mt-1">
                                <AlertCircle className="h-3 w-3" />
                                Files not found: "{item.fileName}" (Expected {item.expectedFileCount} files)
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  });
                })}
              </div>
            </div>
          )}
          {/* Upload Summary */}
          {excelFile && (
            <div className="p-4 bg-blue-50 border rounded-lg">
              <h4 className="text-sm font-medium text-blue-800 mb-2">Upload Summary</h4>
              <div className="space-y-1 text-sm text-blue-700">
                <div>• Excel Template: {excelFile.name}</div>
                <div>• Documents: {documentFiles.length} file(s)</div>
                {Object.keys(matchedDocuments).length > 0 && (
                  <>
                    <div>• Categories Found: {Object.keys(matchedDocuments).join(', ')}</div>
                    {(() => {
                      let totalItems = 0;
                      let matchedFiles = 0;
                      let missingFiles = 0;
                      let noFileRequired = 0;
                      
                      Object.values(matchedDocuments).forEach((categoryData: any) => {
                        categoryData.forEach((item: any) => {
                          totalItems++;
                          const hasFileName = item.fileName && item.fileName.trim() !== '';
                          if (hasFileName && item.isMatched) {
                            matchedFiles += item.matchedFileCount || 1;
                          } else if (hasFileName && !item.isMatched) {
                            missingFiles += item.expectedFileCount || 1;
                          } else if (!hasFileName) {
                            noFileRequired++;
                          }
                        });
                      });
                      
                      return (
                        <>
                          <div>• Total Materials: {totalItems}</div>
                          <div>• Files Matched: {matchedFiles}</div>
                          <div>• Files Missing: {missingFiles}</div>
                          <div>• No File Required: {noFileRequired}</div>
                        </>
                      );
                    })()}
                  </>
                )}
                {documentFiles.length > 0 && (
                  <div>• Total Size: {(documentFiles.reduce((acc, file) => acc + file.size, 0) / 1024 / 1024).toFixed(2)} MB</div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-3 mt-6 flex-shrink-0">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
            className="min-w-[100px]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!excelFile || isUploading || hasMissingFiles}
            className="bg-[#EB1C37] hover:bg-[#EB1C37]/90 text-white min-w-[100px] disabled:bg-gray-400 disabled:cursor-not-allowed"
            loading={isUploading}
            loadingText="Uploading..."
          >
            {hasMissingFiles ? (
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Fix Missing Files
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Upload
              </div>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 